import React from "react";
import {
  CarryOutTwoTone,
  CheckSquareOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
  ProjectOutlined,
  TeamOutlined,
  SolutionOutlined,
  PlusCircleOutlined,
  LinkOutlined,
  ShoppingOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  Select,
  Card,
  Space,
  Statistic,
  Typography,
  Row,
  Col,
  Descriptions,
  Button,
  Tag,
  Progress,
} from "antd";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import "../utils/chartTheme.scss";
import "./DashboardWrapper.scss";
import {
  BRAND,
  CATEGORICAL,
  STATUS,
  donutOptions,
  barOptions,
} from "../utils/chartTheme";
import {
  toGetDashboard,
  toGetProjects,
  toGetProjectDetails,
  toGetProjectOpertaions,
  toGetProjectOpertaionsStatus,
  toGetInitiativesSummary,
  toGetInitiativesAnalytics,
  toGetInitiativeDetails,
} from "../apis/actions/dashboard.actions";
import { setSelectedView } from "../apis/slices/dashboardSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../apis/store";
import { CSSProperties } from "react";
import TaskTable from "./TasksTable";
import AddNoteModal from "./AddTaskNoteModal";
import Selects from "../components/SelectComponent";
import AddProjectNoteModal from "./AddProjectNoteModal";
import AddInitiativeNoteModal from "./AddInitiativeNoteModal";
import LinksTable from "./LinksTable";
const { Option } = Select;

const viewOptions = [
  { id: "projects", name: "المشاريع" },
  { id: "initiatives", name: "المبادرات" },
];

interface DashboardCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  titleStyle?: CSSProperties;
}

function DashboardWrapper() {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalProjectVisible, setIsModalProjectVisible] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(
    null,
  );

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskName, setSelectedTaskName] = useState<string | null>(null);
  const [selectedInitiative, setSelectedInitiative] = useState<any>(null);
  const [isModalInitiativeVisible, setIsModalInitiativeVisible] =
    useState(false);
  const [selectedInitiativeIdForNote, setSelectedInitiativeIdForNote] =
    useState<string | null>(null);
  const [selectedInitiativeNameForNote, setSelectedInitiativeNameForNote] =
    useState<string | null>(null);

  useEffect(() => {
    dispatch(toGetDashboard());
    dispatch(toGetProjects());
    dispatch(toGetInitiativesSummary());
    dispatch(toGetInitiativesAnalytics());
  }, [dispatch]);

  const handleAddNote = (taskId: string, taskName: string) => {
    setSelectedTaskId(taskId);
    setSelectedTaskName(taskName);
    setIsModalVisible(true);
  };
  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedTaskId(null);
    setSelectedTaskName(null);
  };
  const handleProjectModalClose = () => {
    setIsModalProjectVisible(false);
    setSelectedProjectId(null);
    setSelectedProjectName(null);
  };
  const handleAddProjectNote = () => {
    setSelectedTaskId(selectedProject?.id);
    setSelectedTaskName(selectedProject?.name);
    setIsModalProjectVisible(true);
  };
  const handleInitiativeModalClose = () => {
    setIsModalInitiativeVisible(false);
    setSelectedInitiativeIdForNote(null);
    setSelectedInitiativeNameForNote(null);
  };
  const handleAddInitiativeNote = () => {
    setSelectedInitiativeIdForNote(selectedInitiative?.id);
    setSelectedInitiativeNameForNote(selectedInitiative?.name);
    setIsModalInitiativeVisible(true);
  };
  const {
    dashboardData,
    projects,
    projectDetails,
    projectOpertaions,
    projectOpertaionsStatus,
    initiatives,
    initiativesAnalytics,
    initiativeDetails,
    selectedView,
  } = useSelector((state: RootState) => state?.dashboard);

  const handleProjectChange = (value: any) => {
    const project = projects?.list.find((p: any) => p.id === value.value);
    setSelectedProject(project);
    if (project.access_token) {
      dispatch(toGetProjectDetails(project.access_token));
      dispatch(toGetProjectOpertaionsStatus({ project_id: project.id }));
      dispatch(toGetProjectOpertaions({ project_id: project.id }));
    }
  };
  useEffect(() => {
    if (projects?.list?.length > 0) {
      const defaultProject = projects.list[0];
      setSelectedProject(defaultProject);
      if (defaultProject.access_token) {
        dispatch(toGetProjectDetails(defaultProject.access_token));
        dispatch(
          toGetProjectOpertaionsStatus({ project_id: defaultProject.id }),
        );
        dispatch(toGetProjectOpertaions({ project_id: defaultProject.id }));
      }
    }
  }, [projects?.list, dispatch]);

  const handleViewChange = (value: any) => {
    dispatch(setSelectedView(value?.value ?? value));
  };
  const handleInitiativeChange = (value: any) => {
    const initiative = initiatives?.list?.find(
      (i: any) => i.id === value.value,
    );
    setSelectedInitiative(initiative);
    if (initiative?.id) {
      dispatch(toGetInitiativeDetails(initiative.id));
    }
  };
  useEffect(() => {
    if (initiatives?.list?.length > 0) {
      const defaultInitiative = initiatives.list[0];
      setSelectedInitiative(defaultInitiative);
      if (defaultInitiative?.id) {
        dispatch(toGetInitiativeDetails(defaultInitiative.id));
      }
    }
  }, [initiatives?.list, dispatch]);

  const selectedInitiativeAnalytics = initiativesAnalytics?.list?.find(
    (i: any) => i.id === selectedInitiative?.id,
  );

  function iconStyle(color: string) {
    return {
      color,
      backgroundColor: "#f0faf8",
      borderRadius: 20,
      fontSize: 24,
      padding: 8,
    };
  }

  function DashboardCard({
    title,
    value,
    icon,
    titleStyle,
  }: DashboardCardProps) {
    return (
      <Card style={{ flex: 1, height: "100%" }}>
        <Space direction="horizontal">
          {icon}
          <Statistic
            title={
              <Typography.Text style={titleStyle}>{title}</Typography.Text>
            }
            value={value}
          />
        </Space>
      </Card>
    );
  }

  // المهام حسب الحالة (donut)
  const tasksByStateLabels = ["المؤجلة", "المنجزة", "الجديدة", "الملغية", "قيد الإنتظار"];
  const tasksByStateSeries = [
    dashboardData?.list?.tasks_by_state?.delayed ?? 0,
    dashboardData?.list?.tasks_by_state?.done ?? 0,
    dashboardData?.list?.tasks_by_state?.new ?? 0,
    dashboardData?.list?.tasks_by_state?.cancelled ?? 0,
    dashboardData?.list?.tasks_by_state?.pending ?? 0,
  ];
  const tasksByStateOptions = donutOptions(
    tasksByStateLabels,
    CATEGORICAL.slice(0, 5),
  );

  // المهام حسب الأولوية (donut)
  const tasksByPriorityLabels = ["عاجلة", "مهمة", "عادية"];
  const tasksByPrioritySeries = [
    dashboardData?.list?.tasks_by_priority?.urgent ?? 0,
    dashboardData?.list?.tasks_by_priority?.important ?? 0,
    dashboardData?.list?.tasks_by_priority?.normal ?? 0,
  ];
  const tasksByPriorityOptions = donutOptions(
    tasksByPriorityLabels,
    CATEGORICAL.slice(0, 3),
  );

  // المهام حسب المشروع (bar, single series)
  const tasksByProjectCategories =
    dashboardData?.list?.tasks_number_by_project?.map(
      (project: any) => project.project_name,
    ) ?? [];
  const tasksByProjectSeries = [
    {
      name: "تدفق المهام على المشاريع",
      data:
        dashboardData?.list?.tasks_number_by_project?.map(
          (project: any) => project.tasks_number,
        ) ?? [],
    },
  ];
  const tasksByProjectOptions = barOptions({
    categories: tasksByProjectCategories,
    colors: [BRAND.green],
    seriesCount: 1,
  });

  // نسبة مهام كل مشروع حسب اجمالي مهام مشاريع المبادرة (bar, single series, %)
  const tasksPercentByProjectSeries = [
    {
      name: "نسبة مهام كل مشروع حسب اجمالي مهام مشاريع المبادرة (%)",
      data:
        dashboardData?.list?.tasks_number_by_project?.map(
          (project: any) => project.tasks_number_percent,
        ) ?? [],
    },
  ];
  const tasksPercentByProjectOptions = barOptions({
    categories: tasksByProjectCategories,
    colors: [BRAND.slate],
    seriesCount: 1,
    valueSuffix: "%",
  });

  // نسبة تقدم المشاريع حسب المبادرة (bar, single series, %)
  const progressProjectsCategories =
    dashboardData?.list?.projects_progress_percent?.map(
      (project: any) => project.project_name,
    ) ?? [];
  const progressProjectsSeries = [
    {
      name: "نسبة تقدم المشاريع حسب المبادرة (%)",
      data:
        dashboardData?.list?.projects_progress_percent?.map(
          (project: any) => project.progress_percent,
        ) ?? [],
    },
  ];
  const progressProjectsOptions = barOptions({
    categories: progressProjectsCategories,
    colors: [BRAND.teal],
    seriesCount: 1,
    valueSuffix: "%",
  });

  // المستفيدون مقابل الحضور حسب المبادرة (bar, grouped)
  const beneficiariesVsAttendanceCategories =
    initiativesAnalytics?.list?.map((initiative: any) => initiative.name) ?? [];
  const beneficiariesVsAttendanceSeries = [
    {
      name: "المستفيدون",
      data:
        initiativesAnalytics?.list?.map(
          (initiative: any) => initiative.beneficiaries,
        ) ?? [],
    },
    {
      name: "الحضور",
      data:
        initiativesAnalytics?.list?.map(
          (initiative: any) => initiative.attendance_rate?.attendance,
        ) ?? [],
    },
  ];
  const beneficiariesVsAttendanceOptions = barOptions({
    categories: beneficiariesVsAttendanceCategories,
    colors: [CATEGORICAL[0], BRAND.slate],
    seriesCount: 2,
  });

  // المستفيدون حسب الجنس (donut)
  const initiativeGenderLabels = ["ذكر", "أنثى", "غير ذلك"];
  const initiativeGenderSeries = [
    selectedInitiativeAnalytics?.gender?.male?.count ?? 0,
    selectedInitiativeAnalytics?.gender?.female?.count ?? 0,
    selectedInitiativeAnalytics?.gender?.other?.count ?? 0,
  ];
  const initiativeGenderOptions = donutOptions(
    initiativeGenderLabels,
    [CATEGORICAL[0], CATEGORICAL[6], BRAND.slate],
  );

  // مؤشر نسبة المستلمين (donut)
  const deliveryRateLabels = ["المستفيدون", "المستلمون"];
  const deliveryRateSeries = [
    selectedInitiativeAnalytics?.delivery_rate?.beneficiaries ?? 0,
    selectedInitiativeAnalytics?.delivery_rate?.recipients ?? 0,
  ];
  const deliveryRateOptions = donutOptions(
    deliveryRateLabels,
    [CATEGORICAL[0], BRAND.slate],
  );

  // المنتجات المخططة مقابل المسلمة (bar, grouped)
  const productsPlannedVsDeliveredCategories =
    selectedInitiativeAnalytics?.products?.map(
      (product: any) => product.name,
    ) ?? [];
  const productsPlannedVsDeliveredSeries = [
    {
      name: "مخطط له",
      data:
        selectedInitiativeAnalytics?.products?.map(
          (product: any) => product.planned_quantity,
        ) ?? [],
    },
    {
      name: "تم التسليم",
      data:
        selectedInitiativeAnalytics?.products?.map(
          (product: any) => product.delivered_quantity,
        ) ?? [],
    },
  ];
  const productsPlannedVsDeliveredOptions = barOptions({
    categories: productsPlannedVsDeliveredCategories,
    colors: [CATEGORICAL[0], BRAND.slate],
    seriesCount: 2,
  });

  // العمليات حسب الإنجاز (bar, grouped)
  const operationsCategories =
    projectOpertaions?.list?.map((op: any) => op.operation_name) ?? [];
  const operationsSeries = [
    {
      name: "المستهدف",
      data: projectOpertaions?.list?.map((op: any) => op.target) ?? [],
    },
    {
      name: "المنجز",
      data: projectOpertaions?.list?.map((op: any) => op.achieved) ?? [],
    },
  ];
  const operationsOptions = barOptions({
    categories: operationsCategories,
    colors: [CATEGORICAL[0], BRAND.slate],
    seriesCount: 2,
  });

  // حالة العمليات (donut, status colors)
  const operationsStatusLabels = ["في المسار", "في خطر", "متأخر"];
  const operationsStatusSeries = [
    projectOpertaionsStatus?.list[0]?.on_track || 0,
    projectOpertaionsStatus?.list[0]?.on_risk || 0,
    projectOpertaionsStatus?.list[0]?.delayed || 0,
  ];
  const operationsStatusOptions = donutOptions(
    operationsStatusLabels,
    [STATUS.good, STATUS.warning, STATUS.critical],
    { valueSuffix: "%" },
  );

  return (
    <Space
      size={20}
      direction="vertical"
      style={{
        width: "100%",
        direction: "rtl",
        marginTop: "2rem",
        marginBottom: "2rem",
      }}
    >
      <Row
        gutter={[16, 16]}
        justify="space-between"
        style={{ marginRight: "1rem", marginLeft: "1rem" }}
      >
        <Col xs={24} sm={24} md={12} lg={4}>
          <Selects
            style={{ width: "100%" }}
            allowClear={false}
            value={selectedView}
            defaultValue="projects"
            onChange={handleViewChange}
            options={viewOptions}
          />
        </Col>
        {selectedView === "initiatives" && (
          <Col xs={24} sm={24} md={24} lg={8}>
            <Selects
              style={{ width: "100%" }}
              allowClear={false}
              selectPlaceholder="اختر المبادرة"
              onChange={handleInitiativeChange}
              value={selectedInitiative?.id}
              options={initiatives?.list?.map((initiative: any) => ({
                id: initiative.id,
                name: initiative.name,
              }))}
            />
          </Col>
        )}
      </Row>

      {selectedView === "initiatives" && (
        <>

          {/* Responsive Grid Layout for Initiative Cards */}
          <Row
            gutter={[16, 16]}
            justify={"space-between"}
            style={{ marginRight: "1rem", marginLeft: "1rem" }}
          >
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<ProjectOutlined style={iconStyle("green")} />}
                title={"عدد المبادرات"}
                value={initiatives?.list?.length}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<TeamOutlined style={iconStyle("#28a745")} />}
                title={"عدد المستفيدين"}
                value={selectedInitiativeAnalytics?.beneficiaries}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<ShoppingOutlined style={iconStyle("#20c997")} />}
                title={"عدد المنتجات"}
                value={selectedInitiativeAnalytics?.products?.length}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<SolutionOutlined style={iconStyle("green")} />}
                title={"عدد المتطوعين"}
                value={selectedInitiativeAnalytics?.volunteers}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<ClockCircleOutlined style={iconStyle("#28a745")} />}
                title={"عدد ساعات التطوع"}
                value={selectedInitiativeAnalytics?.volunteering_hours}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
              <DashboardCard
                icon={<CalendarOutlined style={iconStyle("#20c997")} />}
                title={"عدد ايام المبادرة"}
                value={selectedInitiativeAnalytics?.duration_days}
                titleStyle={{
                  fontSize: "20px",
                  color: "#0d6e5f",
                  textAlign: "right",
                }}
              />
            </Col>
          </Row>

          {/* Initiative details */}
          <Row
            gutter={[16, 16]}
            style={{ marginRight: "1rem", marginLeft: "1rem" }}
          >
            <Col xs={24} sm={24} md={24} lg={24}>
              <Card
                bordered={false}
                style={{ flex: 1, height: "100%" }}
                title={
                  <div>
                    <span style={{ marginLeft: "1rem" }}>تفاصيل المبادرة</span>
                    <Button
                      style={{ width: "150px" }}
                      className="add-note-button"
                      icon={<PlusCircleOutlined />}
                      onClick={() => handleAddInitiativeNote()}
                    >
                      ملاحظة
                    </Button>
                  </div>
                }
              >
                {selectedInitiative ? (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                      <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                        <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                          المعلومات الأساسية
                        </Typography.Title>
                        <Descriptions column={1}>
                          <Descriptions.Item label="اسم المبادرة">
                            {initiativeDetails?.list?.name}
                          </Descriptions.Item>
                          <Descriptions.Item label="الهدف منها">
                            {initiativeDetails?.list?.objective}
                          </Descriptions.Item>
                          <Descriptions.Item label="الموقع">
                            {initiativeDetails?.list?.position}
                          </Descriptions.Item>
                          <Descriptions.Item label="موقع جوجل">
                            {initiativeDetails?.list?.map_position && (
                              <a
                                href={initiativeDetails?.list?.map_position}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <LinkOutlined style={{ marginLeft: 5 }} />
                                رابط موقع جوجل
                              </a>
                            )}
                          </Descriptions.Item>
                        </Descriptions>
                        <Descriptions
                          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                          style={{ marginTop: "1rem", marginBottom: "1rem" }}
                        >
                          <Descriptions.Item label="تاريخ البداية">
                            {initiativeDetails?.list?.date_start}
                          </Descriptions.Item>
                          <Descriptions.Item label="تاريخ النهاية">
                            {initiativeDetails?.list?.date}
                          </Descriptions.Item>
                        </Descriptions>
                        <Descriptions
                          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                          style={{ marginTop: "1rem", marginBottom: "1rem" }}
                        >
                          <Descriptions.Item label="عدد المتطوعين">
                            {initiativeDetails?.list?.volunteer_number}
                          </Descriptions.Item>
                          <Descriptions.Item label="ساعات عمل المتطوعين">
                            {initiativeDetails?.list?.volunteer_hours}
                          </Descriptions.Item>
                        </Descriptions>
                        <Descriptions
                          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                          style={{ marginTop: "1rem", marginBottom: "1rem" }}
                        >
                          <Descriptions.Item label="عدد الموظفين">
                            {initiativeDetails?.list?.employee_count}
                          </Descriptions.Item>
                          <Descriptions.Item label="ساعات عمل الموظفين">
                            {initiativeDetails?.list?.working_hours}
                          </Descriptions.Item>
                        </Descriptions>
                        <Descriptions column={1}>
                          <Descriptions.Item label="رابط البث المباشر">
                            {initiativeDetails?.list?.live_stream_link ? (
                              <a
                                href={initiativeDetails?.list?.live_stream_link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <LinkOutlined style={{ marginLeft: 5 }} />
                                الرابط
                              </a>
                            ) : (
                              "غير متوفر"
                            )}
                          </Descriptions.Item>
                        </Descriptions>
                        <Descriptions column={1}>
                          <Descriptions.Item
                            label="علامات التصنيف المقترحة"
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                maxWidth: "100%",
                                gap: "8px",
                              }}
                            >
                              {initiativeDetails?.list?.tag_ids?.map(
                                (tagId: any) => (
                                  <Tag key={tagId.id} className="tag--component">
                                    <span className="ghaith--tag-content">
                                      {tagId.name}
                                    </span>
                                  </Tag>
                                ),
                              )}
                            </div>
                          </Descriptions.Item>
                          <Descriptions.Item label="نسبة تقدم المبادرة">
                            {initiativeDetails?.list?.project_progress_percent !==
                            undefined ? (
                              <Progress
                                percent={
                                  initiativeDetails.list.project_progress_percent
                                }
                                status="active"
                                strokeColor={{
                                  "0%": "#108ee9",
                                  "100%": "#87d068",
                                }}
                                showInfo
                                format={(percent) => `${percent} %`}
                              />
                            ) : null}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                      <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                        <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                          المهام
                        </Typography.Title>
                        <TaskTable
                          tasks={initiativeDetails?.list?.task_ids || []}
                          onAddNote={handleAddNote}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                      <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                        <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                          روابط الصور و الفيديوهات
                        </Typography.Title>
                        <LinksTable
                          Links={initiativeDetails?.list?.media_link_ids || []}
                        />
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <Typography.Text type="secondary">
                    يرجى اختيار مبادرة لعرض التفاصيل.
                  </Typography.Text>
                )}
              </Card>
            </Col>
          </Row>

          {/* Initiative charts */}
          <Row
            gutter={[16, 16]}
            style={{ marginRight: "1rem", marginLeft: "1rem" }}
          >
            <Col xs={24} sm={24} md={12} lg={12}>
              <Card title="المستفيدون مقابل الحضور حسب المبادرة">
                <div
                  style={{
                    width: "100%",
                    height: "400px",
                    position: "relative",
                  }}
                >
                  <Chart
                    type="bar"
                    height="100%"
                    series={beneficiariesVsAttendanceSeries}
                    options={beneficiariesVsAttendanceOptions}
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Card title="المستفيدون حسب الجنس">
                <div
                  style={{
                    width: "100%",
                    height: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Chart
                    type="donut"
                    width={300}
                    height={300}
                    series={initiativeGenderSeries}
                    options={initiativeGenderOptions}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Initiative delivery rate & products chart */}
          <Row
            gutter={[16, 16]}
            style={{ marginRight: "1rem", marginLeft: "1rem" }}
          >
            <Col xs={24} sm={24} md={12} lg={12}>
              <Card title="مؤشر نسبة المستلمين">
                <div
                  style={{
                    width: "100%",
                    height: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Chart
                    type="donut"
                    width={300}
                    height={300}
                    series={deliveryRateSeries}
                    options={deliveryRateOptions}
                  />
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Card title="المنتجات المخططة مقابل المسلمة">
                <div
                  style={{
                    width: "100%",
                    height: "400px",
                    position: "relative",
                  }}
                >
                  <Chart
                    type="bar"
                    height="100%"
                    series={productsPlannedVsDeliveredSeries}
                    options={productsPlannedVsDeliveredOptions}
                  />
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Responsive Grid Layout for Cards */}
      {selectedView !== "initiatives" && (
      <>
      <Row
        gutter={[16, 16]}
        justify={"space-between"}
        style={{ marginRight: "1rem", marginLeft: "1rem" }}
      >
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<ProjectOutlined style={iconStyle("green")} />}
            title={"عدد المشاريع"}
            value={dashboardData?.list?.projects_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<UnorderedListOutlined style={iconStyle("#28a745")} />}
            title={"عدد المهام"}
            value={dashboardData?.list?.tasks_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }} // Custom style here
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<TeamOutlined style={iconStyle("#20c997")} />}
            title={"عدد العاملين"}
            value={dashboardData?.list?.workers_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }} // Custom style here
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<ClockCircleOutlined style={iconStyle("green")} />}
            title={"عدد ساعات العمل "}
            value={dashboardData?.list?.working_hours_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }} // Custom style here
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<SolutionOutlined style={iconStyle("#28a745")} />}
            title={"عدد ساعات التطوع"}
            value={dashboardData?.list?.volunteer_hours_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }} // Custom style here
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={4} className="dashboard-stat-card-col">
          <DashboardCard
            icon={<CheckSquareOutlined style={iconStyle("#20c997")} />}
            title={"عدد المهام المنجزة"}
            value={dashboardData?.list?.done_tasks_number}
            titleStyle={{
              fontSize: "20px",
              color: "#0d6e5f",
              textAlign: "right",
            }} // Custom style here
          />
        </Col>
      </Row>
      <Row
        gutter={[16, 16]}
        style={{ marginRight: "1rem", marginLeft: "1rem" }}
      >
        <Col xs={24} sm={24} md={24} lg={24}>
          <Card
            bordered={false}
            style={{ flex: 1, height: "100%" }}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "15px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <span style={{ marginLeft: "1rem" }}>تفاصيل المشروع</span>
                  <Button
                    style={{ width: "150px" }}
                    className="add-note-button"
                    icon={<PlusCircleOutlined />}
                    onClick={() => handleAddProjectNote()}
                  >
                    ملاحظة
                  </Button>{" "}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "end",
                    textAlign: "end",
                    justifyContent: "flex-end",
                    gap: "15px",
                    flexWrap: "wrap",
                  }}
                >
                  <Selects
                    style={{ maxWidth: "100%" }}
                    allowClear={false}
                    selectPlaceholder="اختر مشروع"
                    onChange={handleProjectChange}
                    value={selectedProject?.id}
                    options={projects?.list?.map((project: any) => ({
                      id: project.id,
                      name: project.name,
                    }))}
                  />
                </div>
              </div>
            }
            extra={
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              ></div>
            }
          >
            {selectedProject ? (
              <Row gutter={[16, 16]}>
                {/* Section 1: Basic Project Info */}
                <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                  <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                    <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                      المعلومات الأساسية
                    </Typography.Title>
                    <Descriptions column={1}>
                      <Descriptions.Item label="اسم المشروع">
                        {selectedProject.name}
                      </Descriptions.Item>
                      <Descriptions.Item label="الهدف منه">
                        {projectDetails?.list?.purpose}
                      </Descriptions.Item>
                      <Descriptions.Item label="الموقع">
                        {projectDetails?.list?.position}
                      </Descriptions.Item>
                      <Descriptions.Item label="موقع جوجل">
                        {projectDetails?.list?.map_position && (
                          <a
                            href={projectDetails?.list?.map_position}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LinkOutlined style={{ marginLeft: 5 }} />
                            رابط موقع جوجل
                          </a>
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                      column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <Descriptions.Item label="تاريخ البداية">
                        {projectDetails?.list?.date_start !== "False" &&
                          projectDetails?.list?.date_start}
                      </Descriptions.Item>
                      <Descriptions.Item label="تاريخ النهاية">
                        {projectDetails?.list?.date !== "False" &&
                          projectDetails?.list?.date}
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                      column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <Descriptions.Item label="عدد المتطوعين">
                        {projectDetails?.list?.volunteers_target_number}
                      </Descriptions.Item>
                      <Descriptions.Item label="ساعات عمل المتطوعين">
                        {projectDetails?.list?.target_volunteer_hours}
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                      column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <Descriptions.Item label="عدد الموظفين">
                        {projectDetails?.list?.employees_target_number}
                      </Descriptions.Item>
                      <Descriptions.Item label="ساعات عمل الموظفين">
                        {projectDetails?.list?.target_employees_hours}
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions column={1}>
                      <Descriptions.Item label="رابط البث المباشر">
                        {projectDetails?.list?.live_stream_link ? (
                          <>
                            <a
                              href={projectDetails?.list?.live_stream_link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <LinkOutlined style={{ marginLeft: 5 }} />
                              الرابط
                            </a>
                          </>
                        ) : (
                          "غير متوفر"
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                    {/*
                    <Descriptions
                      column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }}
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <Descriptions.Item label="عدد السجاد">
                        {projectDetails?.list?.number_carpets}
                      </Descriptions.Item>
                      <Descriptions.Item label="عدد السفر">
                        {projectDetails?.list?.dining_mats_number}
                      </Descriptions.Item>
                    </Descriptions>
                    <Descriptions
                      column={1}
                      style={{ marginTop: "1rem", marginBottom: "1rem" }}
                    >
                      <Descriptions.Item label="عدد الوجبات اليومية المقترحة">
                        {projectDetails?.list?.proposed_number_daily_meals}
                      </Descriptions.Item>
                      <Descriptions.Item label="عدد العبوات اليومية المقترحة">
                        {projectDetails?.list?.proposed_number_daily_packages}
                      </Descriptions.Item>
                    </Descriptions>
                    */}
                    <Descriptions column={1}>
                      <Descriptions.Item
                        label="علامات التصنيف المقترحة"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            maxWidth: "100%",
                            gap: "8px",
                          }}
                        >
                          {projectDetails?.list?.tag_ids?.map((tagId: any) => (
                            <Tag key={tagId.id} className="tag--component">
                              <span className="ghaith--tag-content">
                                {tagId.name}
                              </span>
                            </Tag>
                          ))}
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="نسبة تقدم المشروع">
                        {projectDetails?.list?.project_progress_percent !==
                        undefined ? (
                          <Progress
                            percent={
                              projectDetails.list.project_progress_percent
                            }
                            status="active"
                            strokeColor={{
                              "0%": "#108ee9",
                              "100%": "#87d068",
                            }}
                            showInfo
                            format={(percent) => `${percent} %`}
                          />
                        ) : null}
                      </Descriptions.Item>
                      ;
                    </Descriptions>
                  </Card>
                </Col>

                {/* Section 3: Additional Details */}
                <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                  <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                    <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                      المهام
                    </Typography.Title>
                    <TaskTable
                      tasks={projectDetails?.list?.task_ids || []}
                      onAddNote={handleAddNote}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} className="dashboard-detail-card-col">
                  <Card bordered={false} style={{ flex: 1, height: "100%" }}>
                    <Typography.Title level={5} style={{ color: "#0d6e5f" }}>
                      روابط الصور و الفيديوهات
                    </Typography.Title>
                    <LinksTable
                      Links={projectDetails?.list?.media_link_ids || []}
                    />
                  </Card>
                </Col>
              </Row>
            ) : (
              <Typography.Text type="secondary">
                يرجى اختيار مشروع لعرض التفاصيل.
              </Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* New Row for Pie Charts */}
      <Row
        gutter={[16, 16]}
        style={{ marginRight: "1rem", marginLeft: "1rem" }}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card title="المهام حسب الحالة">
            <div style={{ width: "300px", height: "300px", margin: "0 auto" }}>
              <Chart
                type="donut"
                width={300}
                height={300}
                series={tasksByStateSeries}
                options={tasksByStateOptions}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card title="المهام حسب الأولوية">
            <div style={{ width: "300px", height: "300px", margin: "0 auto" }}>
              <Chart
                type="donut"
                width={300}
                height={300}
                series={tasksByPrioritySeries}
                options={tasksByPriorityOptions}
              />
            </div>
          </Card>
        </Col>
      </Row>
      {/* New Row for Project Operations Charts */}
      <Row
        gutter={[16, 16]}
        style={{ marginRight: "1rem", marginLeft: "1rem", marginTop: "1rem" }}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card title="العمليات حسب الإنجاز">
            <div
              style={{ width: "100%", height: "400px", position: "relative" }}
            >
              <Chart
                type="bar"
                height="100%"
                series={operationsSeries}
                options={operationsOptions}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card title="حالة العمليات">
            <div
              style={{
                width: "300px",
                minHeight: "400px",
                margin: "0 auto",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Chart
                type="donut"
                width={300}
                height={300}
                series={operationsStatusSeries}
                options={operationsStatusOptions}
              />
            </div>
          </Card>
        </Col>
      </Row>
      {/* New Row for Bar Charts */}
      <Row
        gutter={[16, 16]}
        style={{ marginRight: "1rem", marginLeft: "1rem" }}
      >
        <Col xs={24} sm={24} md={12} lg={8}>
          <Card title="المهام حسب المشروع">
            <div
              style={{ width: "100%", height: "400px", position: "relative" }}
            >
              <Chart
                type="bar"
                height="100%"
                series={tasksByProjectSeries}
                options={tasksByProjectOptions}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Card title="نسبة تقدم المشاريع حسب المبادرة">
            <div
              style={{ width: "100%", height: "400px", position: "relative" }}
            >
              <Chart
                type="bar"
                height="100%"
                series={progressProjectsSeries}
                options={progressProjectsOptions}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Card title="نسبة مهام كل مشروع حسب اجمالي مهام مشاريع المبادرة">
            <div
              style={{ width: "100%", height: "400px", position: "relative" }}
            >
              <Chart
                type="bar"
                height="100%"
                series={tasksPercentByProjectSeries}
                options={tasksPercentByProjectOptions}
              />
            </div>
          </Card>
        </Col>
      </Row>
      </>
      )}
      <AddNoteModal
        visible={isModalVisible}
        onClose={handleModalClose}
        taskId={selectedTaskId}
        taskName={selectedTaskName}
      />
      <AddProjectNoteModal
        visible={isModalProjectVisible}
        onClose={handleProjectModalClose}
        ProjectId={selectedTaskId}
        ProjectName={selectedTaskName}
      />
      <AddInitiativeNoteModal
        visible={isModalInitiativeVisible}
        onClose={handleInitiativeModalClose}
        InitiativeId={selectedInitiativeIdForNote}
        InitiativeName={selectedInitiativeNameForNote}
      />
    </Space>
  );
}

export default DashboardWrapper;
